import { useEffect, useState } from "react";
import { Phone, WhatsappLogo, InstagramLogo, FacebookLogo, TiktokLogo, Globe, Tag, CheckCircle, LockSimple } from "@phosphor-icons/react";
import { img, money, waLink, fmtDate } from "@/lib/api";
import { CheckinForm } from "./CheckinForm";
import { InfoBlocks } from "./InfoBlocks";
import { Explore, TakeHome } from "./Explore";
import { LogoMark } from "@/components/common";

const text = { color: "var(--t-text)" };
const muted = { color: "var(--t-muted)" };

function HostCard({ host }) {
  if (!host?.name) return null;
  const socials = [[host.instagram, InstagramLogo, "instagram"], [host.facebook, FacebookLogo, "facebook"], [host.tiktok, TiktokLogo, "tiktok"], [host.website, Globe, "website"]].filter((s) => s[0]);
  return (
    <section className="rounded-3xl border p-5" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }} data-testid="guest-host-card">
      <div className="flex items-center gap-4">
        {host.photo ? <img src={img(host.photo)} alt={host.name} className="h-16 w-16 rounded-full object-cover" /> : <div className="grid h-16 w-16 place-items-center rounded-full font-display text-2xl" style={{ background: "var(--t-soft)", color: "var(--t-primary)" }}>{host.name[0]}</div>}
        <div><p className="label-xs" style={muted}>Your host</p><p className="font-display text-xl" style={text}>{host.name}</p></div>
      </div>
      {host.bio && <p className="mt-3 text-sm leading-relaxed" style={muted}>{host.bio}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {host.phone && <a href={`tel:${host.phone}`} data-testid="host-call-button" className="inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold" style={{ borderColor: "var(--t-border)", ...text }}><Phone size={16} /> Call</a>}
        {host.whatsapp && <a href={waLink(host.whatsapp)} target="_blank" rel="noreferrer" data-testid="host-whatsapp-button" className="inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><WhatsappLogo size={16} weight="fill" /> WhatsApp</a>}
        {socials.map(([u, Icon, k]) => <a key={k} href={u} target="_blank" rel="noreferrer" data-testid={`host-social-${k}`} className="grid h-11 w-11 place-items-center rounded-full border" style={{ borderColor: "var(--t-border)", ...text }}><Icon size={18} /></a>)}
      </div>
    </section>
  );
}

function Rates({ rates }) {
  if (!rates?.show) return null;
  const rows = [["Per night", rates.nightly], ["Weekend night", rates.weekend], ["Per week", rates.weekly], ["Per month", rates.monthly], ["Cleaning fee", rates.cleaning_fee], ["Deposit", rates.deposit], ...(rates.extras || []).map((e) => [e.label, e.amount])].filter((r) => r[0] && r[1]);
  if (!rows.length) return null;
  return (
    <section className="rounded-3xl border p-5" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }} data-testid="guest-rates-card">
      <p className="label-xs" style={{ color: "var(--t-primary)" }}>Rates</p>
      <div className="mt-2 divide-y" style={{ borderColor: "var(--t-border)" }}>
        {rows.map(([l, v], i) => <div key={i} className="flex justify-between py-2.5 text-sm"><span style={muted}>{l}</span><span className="font-semibold" style={text}>{money(rates.currency, v)}</span></div>)}
      </div>
    </section>
  );
}

function OfferBanner({ offer }) {
  if (!offer?.enabled || !offer.headline) return null;
  return (
    <section className="relative overflow-hidden rounded-3xl p-5" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="guest-offer-banner">
      {offer.badge && <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider"><Tag size={12} weight="fill" /> {offer.badge}</span>}
      <p className="mt-3 font-display text-xl leading-snug">{offer.headline}</p>
      <div className="mt-3 flex items-center justify-between text-sm">
        {offer.code && <span>Code <b className="rounded-md bg-white/20 px-2 py-0.5 font-mono tracking-wider">{offer.code}</b></span>}
        {offer.ends && <span className="opacity-80">Until {fmtDate(offer.ends)}</span>}
      </div>
    </section>
  );
}

export function GuestView({ property: p, info: initialInfo, locked, onCheckin, onReturning, previewStage, unlockedRef }) {
  const [info, setInfo] = useState(initialInfo);
  const [done, setDone] = useState(unlockedRef || null);
  useEffect(() => setInfo(initialInfo), [initialInfo]);
  useEffect(() => setDone(unlockedRef || null), [unlockedRef]);

  const preview = previewStage !== undefined;
  const showForm = preview ? previewStage === "form" && p.form?.enabled : locked && !info;
  const showInfo = preview ? !showForm : !!info;

  const submit = async (form, file) => {
    const r = await onCheckin(form, file);
    setInfo(r.info);
    setDone({ ref: r.ref, name: form.name.split(" ")[0] });
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };
  const back = async (last4) => {
    const r = await onReturning(last4);
    setInfo(r.info);
    setDone({ ref: null, name: r.name });
  };

  return (
    <div data-theme={p.theme || "savannah"} className="relative min-h-full" style={{ background: "var(--t-bg)" }} data-testid="guest-view">
      {p.background && <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: `url(${img(p.background)})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
      <div className="relative mx-auto max-w-md pb-10">
        <header className="relative h-72 overflow-hidden">
          {p.cover ? <img src={img(p.cover)} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ background: "var(--t-soft)" }} />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <p className="label-xs opacity-80">Welcome to</p>
            <h1 className="mt-1 font-display text-3xl font-medium leading-tight" data-testid="guest-property-name">{p.name}</h1>
          </div>
        </header>
        <div className="space-y-4 px-4 pt-4">
          {done && (
            <div className="rise flex items-start gap-3 rounded-3xl p-4" style={{ background: "var(--t-soft)" }} data-testid="checkin-success">
              <CheckCircle size={26} weight="fill" style={{ color: "var(--t-primary)" }} className="shrink-0" />
              <div style={text}><p className="font-semibold">{done.ref ? `Thanks${done.name ? `, ${done.name}` : ""}! You're checked in.` : `Welcome back${done.name ? `, ${done.name}` : ""}!`}</p>{done.ref && <p className="text-sm" style={muted}>Reference <b data-testid="checkin-reference">{done.ref}</b></p>}</div>
            </div>
          )}
          {p.description && <p className="px-1 text-[15px] leading-relaxed" style={muted}>{p.description}</p>}
          <OfferBanner offer={p.offer} />
          {showForm && <CheckinForm form={p.form} onSubmit={submit} onReturning={back} disabled={preview} />}
          {showForm && (
            <div className="flex items-center gap-3 rounded-3xl border border-dashed p-4 text-sm" style={{ borderColor: "var(--t-border)", ...muted }} data-testid="guest-locked-hint">
              <LockSimple size={20} /> Wi-Fi, directions and house info unlock after you check in.
            </div>
          )}
          {showInfo && info && <InfoBlocks info={info} />}
          <Explore info={showInfo ? info : null} />
          <HostCard host={p.host} />
          <Rates rates={p.rates} />
          <p className="flex items-center justify-center gap-1.5 pt-4 text-xs" style={muted}>Guest page by <LogoMark size={14} /> <b>hostkey.online</b></p>
          {showInfo && <TakeHome info={info} />}
        </div>
      </div>
    </div>
  );
}
